import React, { useState, useEffect } from "react";
import { BarChart3, RefreshCw, Database, Clock } from "lucide-react";
import LoadingSpinner from "../../components/LoadingSpinner";
import ErrorMessage from "../../components/ErrorMessage";
import PageHeader from "../../components/PageHeader";
import AdminDashboard from "./AdminDashboard";
import PostOfficeDashboard from "./PostOfficeDashboard";
import { OrderStats, User } from "./types";
import { API_BASE_URL } from "../../utils/apiConfig";
import { useLanguage } from "../../contexts/LanguageContext";

interface CacheInfo {
  lastUpdate: string | null;
  totalOrders: number;
  refreshInterval: number;
  isLoading: boolean;
}

interface DashboardMainProps {
  currentUser?: User;
}

const DashboardMain: React.FC<DashboardMainProps> = ({ currentUser }) => {
  const { t } = useLanguage();
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timePeriod, setTimePeriod] = useState<string>("all-time");
  const [cacheInfo, setCacheInfo] = useState<CacheInfo | null>(null);

  // Check permission (must be after hooks)
  const hasPermission = currentUser?.permissions?.includes("dashboard.view") || false;

  const fetchCacheInfo = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/enhanced-orders/cache/info`);
      const data = await response.json();
      
      if (data.success) {
        setCacheInfo(data.data);
      } else {
        // Cache is disabled or not available
        setCacheInfo(null);
      }
    } catch (err) {
      // Cache is disabled or not available
      setCacheInfo(null);
      console.log("Cache service not available (disabled)");
    }
  };

  const refreshCache = async () => {
    try {
      setCacheInfo((prev) => prev ? { ...prev, isLoading: true } : null);
      
      const response = await fetch(`${API_BASE_URL}/api/enhanced-orders/cache/refresh`, {
        method: "POST",
      });
      const data = await response.json();
      
      if (data.success) {
        // Fetch updated cache info
        await fetchCacheInfo();
        // Refresh stats to show new data
        await fetchStats();
      }
    } catch (err) {
      console.error("Error refreshing cache:", err);
    }
  };

  const fetchStats = async () => {
    // Check permission before fetching
    if (!hasPermission) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Build API URL with filters
      let apiUrl = `${API_BASE_URL}/api/enhanced-orders/paginated?page=1&limit=50&period=${timePeriod}`;

      // Add country filter for post office managers
      if (
        currentUser?.role === "country-post-manager" &&
        currentUser.countries && 
        currentUser.countries.length > 0
      ) {
        const countryParams = currentUser.countries
          .map((c) => `country=${c.toLowerCase()}`)
          .join("&");
        apiUrl += `&${countryParams}`;

        // For post office dashboards, align with DeliveredStatistics: filter by shippingDate
        apiUrl += `&timePeriodBy=shippingDate`;
      }

      const response = await fetch(apiUrl);
      const data = await response.json();

      if (data.success) {
        // Use stats directly from backend (already filtered by country if applicable)
        setStats(data.data.stats);
        setError(null);
        
        // Also fetch cache info
        fetchCacheInfo();
      } else {
        setError(data.error || t('dashboard.failedToFetch'));
      }
    } catch (err) {
      setError(t('dashboard.errorConnecting'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchCacheInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timePeriod, currentUser]);

  useEffect(() => {
    // Poll cache info every minute to show real-time cache status
    const interval = setInterval(() => {
      fetchCacheInfo();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Check permission after all hooks - return early if no permission
  if (!hasPermission) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 text-red-400 mx-auto mb-4">🚫</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {t('dashboard.accessDenied')}
          </h3>
          <p className="text-gray-600">
            {t('dashboard.noPermission')}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error} onRetry={fetchStats} />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <PageHeader
        icon={BarChart3}
        title={t('dashboard.title')}
        description={t('dashboard.description')}
        tabs={[]}
        activeTab=""
        onTabChange={() => {}}
        customButtons={[
          {
            id: "time-period",
            label: t('dashboard.timePeriod'),
            type: "select",
            value: timePeriod,
            onChange: setTimePeriod,
            options: [
              { value: "all-time", label: t('dashboard.allTime') },
              { value: "today", label: t('time.today') },
              { value: "this-week", label: t('time.thisWeek') },
              { value: "this-month", label: t('time.thisMonth') },
              { value: "this-year", label: t('time.thisYear') },
            ],
          },
          {
            id: "refresh-cache",
            label: t('dashboard.refreshCache'),
            icon: Database,
            onClick: refreshCache,
            variant: "secondary",
            disabled: loading || cacheInfo?.isLoading,
          },
          {
            id: "refresh",
            label: t('dashboard.refresh'),
            icon: RefreshCw,
            onClick: fetchStats,
            variant: "primary",
            disabled: loading,
          },
        ]}
      />

      {/* Dashboard Content with same margins as header */}
      <div className="w-full lg:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <RefreshCw className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: "#264a3e" }} />
              <p className="text-gray-600">{t('dashboard.loadingStatistics')}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Cache Status Info */}
            {cacheInfo && (
              <div className="mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 shadow-sm">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <Database className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-900">
                        {t('dashboard.cacheStatus')}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        cacheInfo.isLoading 
                          ? "bg-yellow-100 text-yellow-800" 
                          : "bg-green-100 text-green-800"
                      }`}>
                        {cacheInfo.isLoading ? t('dashboard.refreshing') : t('dashboard.active')}
                      </span>
                    </div>
                    <div className="h-4 w-px bg-gray-300" />
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <span className="font-medium">{cacheInfo.totalOrders.toLocaleString()}</span>
                      <span>{t('dashboard.ordersCached')}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <Clock className="w-3.5 h-3.5" />
                    <span>
                      {t('dashboard.lastUpdated')}{" "}
                      {cacheInfo.lastUpdate
                        ? new Date(cacheInfo.lastUpdate).toLocaleTimeString()
                        : t('dashboard.never')}
                    </span>
                    <span className="text-gray-400">•</span>
                    <span>{t('dashboard.autoRefresh')} {cacheInfo.refreshInterval}min</span>
                  </div>
                </div>
              </div>
            )}

            {/* Country-specific info for post office managers */}
            {currentUser?.role === "country-post-manager" &&
              currentUser.countries.length > 0 && (
                <div className="mb-4 sm:mb-6 flex items-center justify-start sm:justify-end">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-0 sm:space-x-2">
                    <span className="text-xs sm:text-sm text-gray-500">{t('dashboard.showingDataFor')}</span>
                    <div className="flex flex-wrap items-center gap-1 sm:gap-0 sm:space-x-1">
                      {currentUser.countries.map((country) => (
                        <span
                          key={country}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {country}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

            {/* Render appropriate dashboard based on user role */}
            {currentUser?.role === "country-post-manager" ? (
              <PostOfficeDashboard
                stats={stats}
                currentUser={currentUser}
                timePeriod={timePeriod}
              />
            ) : (
              <AdminDashboard stats={stats} currentUser={currentUser} />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default DashboardMain;
